import { action, makeObservable, observable } from 'mobx';
import { AxiosResponse } from 'axios';
import { useService } from '~/services/service.base';
import { WrapperService } from '~/services/service.wrapper';
import { api } from '~/tools/api/api';
import { EApiUrl } from '~/tools/api/enums';
import { extractServerError } from '~/tools/error';
import { Injectable } from './service.base';
import { ModelUserProfile } from '~/models/model.user.profile';
import { GaCategories, GaEvents } from '~/views/enums';

interface ServerError {
    description: string;
    code?: string;
}

@Injectable()
export class SignInDialogService {
    @observable public open = false;
    @observable public showStep1 = true;
    @observable public showStep2 = false;
    @observable public showStep3 = false;
    @observable public showStep4 = false;
    @observable public btnDisabled = true;
    @observable public showLoading = false;

    @observable public fullNumber: string;
    @observable public phoneNumber: string;
    @observable public phoneAsPassword = false;

    @observable private _lastError: ServerError = { description: '', code: undefined };
    private authService: any;

    constructor() {
        const wrapperService = useService(WrapperService);
        const { authService } = wrapperService;
        this.authService = authService;
        makeObservable(this);
    }

    get lastError(): ServerError {
        return this._lastError;
    }

    @action
    setLastError = (error: ServerError) => {
        this._lastError = error;
    };

    @action
    clearLastError = () => {
        this._lastError = { description: '', code: undefined };
    };

    @action
    handleOpen = (e: any) => {
        e.preventDefault();
        this.open = true;
    };

    @action
    setOpen = (value: boolean) => {
        this.open = value;
    };

    @action
    handleClose = () => {
        this.open = false;
        this.showStep1 = true;
        this.showStep3 = false;
        this.showStep4 = false;
        this.fullNumber = '';
        this.clearLastError();
    };

    @action
    handleOpenStep2 = () => {};

    @action
    handleOpenStep1 = (e: any) => {
        e.preventDefault();
        this.showStep1 = true;
        this.clearLastError();
    };

    @action
    handleOpenStep3 = async (alertDialogSrv: any) => {
        this.authService.sendGaEvent({
            category: GaCategories.loginFlow,
            action: GaEvents.phoneEntered,
            label: JSON.stringify({
                phone_format_valid: true,
                country_code: '+1',
                masked_phone: this.maskPhoneNumber(),
            }),
        });

        const sendResult: boolean = await this.sendVerificationCode();

        if (sendResult) {
            this.setShowStep1(false);
            this.setShowStep3(true);
        } else if (this.lastError.description) {
            alertDialogSrv.handleOpen();
        }
    };

    maskPhoneNumber = () => {
        const phone = this.fullNumber?.replaceAll(/-/gi, '').replaceAll(/\s/gi, '') ?? '';
        return phone.replace(/^(\+\d)(\d+)(\d{4})$/, (_, p1, p2, p3) => {
            return p1 + '*'.repeat(p2.length) + p3;
        });
    };

    @action
    handlePhoneNumberChange = (isValid: boolean, value: string, selectedCountryData: any, fullNumber: string) => {
        this.clearLastError();
        const numericValue = value.replace(/\D/g, '');
        this.phoneNumber = numericValue;
        this.btnDisabled = !isValid;
        this.fullNumber = `+${fullNumber.replace(/\D/g, '')}`;
    };

    sendVerificationCode = async (): Promise<boolean> => {
        this.clearLastError();
        let sendResult = false;
        this.setShowLoading(true);

        try {
            const phone = this.fullNumber.replaceAll(/-/gi, '').replaceAll(/\s/gi, '');
            const startReq = Date.now();
            const response: AxiosResponse = await api.post(EApiUrl.requestSmsCode, { phoneNumber: phone });

            if (response.status === 200) {
                const endtReq = Date.now();
                this.authService.sendGaEvent({
                    category: GaCategories.loginFlow,
                    action: GaEvents.verificationSent,
                    label: JSON.stringify({
                        phone_found: true,
                        masked_phone: this.maskPhoneNumber(),
                        response_time_ms: `${endtReq - startReq} ms`,
                    }),
                });
                sendResult = true;
            }
        } catch (error: any) {
            this.setLastError(extractServerError(error?.response?.data ?? error));

            this.authService.sendGaEvent({
                category: GaCategories.loginFlow,
                action: GaEvents.loginFailed,
                label: JSON.stringify({
                    reason: error?.response?.data,
                    masked_phone: this.maskPhoneNumber(),
                }),
            });
        }
        this.setShowLoading(false);
        return sendResult;
    };

    @action
    setShowStep1 = (value: boolean) => {
        this.showStep1 = value;
    };

    @action
    setShowStep2 = (value: boolean) => {
        this.showStep2 = value;
    };

    @action
    setShowStep3 = (value: boolean) => {
        this.showStep3 = value;
    };

    @action
    setShowStep4 = (value: boolean) => {
        this.showStep4 = value;
    };

    @action
    setShowLoading = (value: boolean) => {
        this.showLoading = value;
    };

    @action
    getAuthToken = async (verificationCode: string): Promise<boolean> => {
        this.clearLastError();
        let verifyResult = false;
        this.setShowLoading(true);

        this.authService.sendGaEvent({
            category: GaCategories.loginFlow,
            action: GaEvents.verificationEntered,
            label: JSON.stringify({
                code_length: verificationCode.length,
            }),
        });

        try {
            const phone = this.fullNumber.replaceAll(/-/gi, '').replaceAll(/\s/gi, '');
            const response: AxiosResponse = await api.post(EApiUrl.verifySmsCode, {
                code: verificationCode,
                phoneNumber: phone,
            });
            if (response.status === 200 && response.data?.token) {
                this.setUserToken(response.data.token);
                const profiles = response.data.profiles || [];
                // const profiles = [
                //     {
                //         uid: 'WG-NC-tKVZHD5JO2lYBG',
                //         displayName: '3437778633',
                //         wdollarBalance: '10',
                //         appCode: 'NC',
                //     },
                //     {
                //         uid: 'WG-NC-tKVZHD5JO2lYBG2',
                //         displayName: '3437778633',
                //         wdollarBalance: '10',
                //         appCode: 'JP',
                //     },
                // ];
                if (profiles.length === 2) {
                    this.setUserProfiles(profiles);
                    this.setShowStep3(false);
                    this.setShowStep4(true);
                } else {
                    await this.loginUser();
                    verifyResult = true;
                }
            }
        } catch (error: any) {
            this.setLastError(extractServerError(error?.response?.data ?? error));

            this.authService.sendGaEvent({
                category: GaCategories.loginFlow,
                action: GaEvents.loginFailed,
                label: JSON.stringify({
                    reason: error?.response?.data,
                    masked_phone: this.maskPhoneNumber(),
                }),
            });
        }
        this.setShowLoading(false);
        return verifyResult;
    };

    @action
    loginUser = async (): Promise<boolean> => {
        let verifyResult = false;
        await this.authService.login(this.getUserToken());
        this.handleClose();
        verifyResult = true;
        window.scrollTo(0, 0);
        return verifyResult;
    };

    @action
    setUserProfiles = (profiles: ModelUserProfile[]) => {
        this.authService.setUserProfiles(profiles);
    };

    @action
    getUserProfiles = (): ModelUserProfile[] => {
        return this.authService.getUserProfiles();
    };

    @action
    setUserToken = (token: string | null) => {
        this.authService.setUserToken(token);
    };

    getUserToken = (): string | null => {
        return this.authService.getUserToken();
    };

    @action
    chooseProfile = async (uid: string, alertDialogSrv: any) => {
        this.clearLastError();
        this.setShowLoading(true);
        const verifyResult = false;
        try {
            const data = {
                profileID: uid,
            };
            const response: AxiosResponse = await api.post(EApiUrl.chooseProfile, data, {
                headers: {
                    Authorization: this.authService.getUserToken(),
                },
            });
            if (response.status === 200 && response.data?.token) {
                await this.loginUser();
            }
        } catch (error: any) {
            this.setLastError(extractServerError(error?.response?.data ?? error));
            alertDialogSrv.handleOpen();
        }
        this.setShowLoading(false);
        return verifyResult;
    };

    handlePhoneEyeClick = () => {
        this.phoneAsPassword = !this.phoneAsPassword;
        const phoneInputType: string = this.phoneAsPassword ? 'password' : 'tel';
        const phoneInput = document.getElementById('sign-in-phone');
        if (phoneInput) {
            phoneInput.setAttribute('type', phoneInputType);
        }
    };

    @action
    setBtnDisabled = (value: boolean) => {
        this.btnDisabled = value;
    };

    @action
    onStepKeyUp = (e: any, alertDialogSrv: any) => {
        if (e.keyCode === 13 && !this.btnDisabled && this.showStep1) {
            this.handleOpenStep3(alertDialogSrv);
        }
    };
}
